import React, {ReactElement, useEffect, useState} from "react";
import {ButtonBase, Typography, useTheme} from "@mui/material";
import {PD_SM, PD_LG, CARD_SHADOW} from "../../shared/dimens";
import {mediumGray} from "../../shared/colors";

export type BackgroundItem = {
  id: string;
  name: string;
  src: string;
  htmlImage: HTMLImageElement;
}

type PlaceholderDef = {
  id: string;
  name: string;
  colors: string[];
  direction?: string; // CSS gradient direction
}

const PLACEHOLDER_BACKGROUNDS: PlaceholderDef[] = [
  {id: "bg_solid_white", name: "White", colors: ["#FFFFFF"]},
  {id: "bg_solid_black", name: "Black", colors: ["#000000"]},
  {id: "bg_solid_blue", name: "Sky Blue", colors: ["#87CEEB"]},
  {id: "bg_solid_coral", name: "Coral", colors: ["#FF7F50"]},
  {id: "bg_solid_mint", name: "Mint", colors: ["#98FF98"]},
  {id: "bg_solid_lavender", name: "Lavender", colors: ["#E6E6FA"]},
  {id: "bg_grad_sunset", name: "Sunset", colors: ["#FF512F", "#F09819"]},
  {id: "bg_grad_ocean", name: "Ocean", colors: ["#2193b0", "#6dd5ed"]},
  {id: "bg_grad_purple", name: "Purple Haze", colors: ["#7B4397", "#DC2430"]},
  {id: "bg_grad_forest", name: "Forest", colors: ["#134E5E", "#71B280"]},
  {id: "bg_grad_peach", name: "Peach", colors: ["#ED4264", "#FFEDBC"]},
  {id: "bg_grad_night", name: "Night Sky", colors: ["#0F2027", "#203A43", "#2C5364"]},
];

function generateBackgroundImage(def: PlaceholderDef, width: number, height: number): Promise<BackgroundItem> {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d")!;
    if (def.colors.length === 1) {
      ctx.fillStyle = def.colors[0];
      ctx.fillRect(0, 0, width, height);
    } else {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      def.colors.forEach((color, i) => {
        gradient.addColorStop(i / (def.colors.length - 1), color);
      });
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }
    const src = canvas.toDataURL("image/png");
    const img = new window.Image();
    img.onload = () => {
      resolve({id: def.id, name: def.name, src, htmlImage: img});
    };
    img.src = src;
  });
}

function BackgroundThumbnail({item, onSelect}: {
  item: BackgroundItem;
  onSelect: (item: BackgroundItem) => void;
}): ReactElement {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={() => onSelect(item)}
      style={{
        overflow: "hidden",
        aspectRatio: "4/3",
        borderRadius: theme.shape.borderRadius,
        border: "2px solid transparent",
        boxShadow: CARD_SHADOW,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "#000066";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent";
      }}>
      <img
        src={item.src}
        alt={item.name}
        style={{width: "100%", height: "100%", objectFit: "cover", display: "block"}}
      />
    </ButtonBase>
  );
}

export function BackgroundPanel(props: {
  onBackgroundSelect: (item: BackgroundItem) => void;
}): ReactElement {
  const {onBackgroundSelect} = props;
  const [backgrounds, setBackgrounds] = useState<BackgroundItem[]>([]);

  useEffect(() => {
    Promise.all(
      PLACEHOLDER_BACKGROUNDS.map(def => generateBackgroundImage(def, 200, 150))
    ).then(setBackgrounds);
  }, []);

  if (backgrounds.length === 0) {
    return (
      <Typography style={{color: mediumGray, textAlign: "center", marginTop: PD_LG}}>
        Loading backgrounds...
      </Typography>
    );
  }

  return (
    <div style={{display: "flex", flexDirection: "column", gap: PD_SM}}>
      <Typography variant="caption" style={{color: mediumGray, textAlign: "center"}}>
        Click a background to add it to your canvas
      </Typography>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: PD_SM,
      }}>
        {backgrounds.map(item => (
          <BackgroundThumbnail key={item.id} item={item} onSelect={onBackgroundSelect}/>
        ))}
      </div>
    </div>
  );
}
